/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/authStore'
import { login as loginApi, logout as logoutApi, getUserProfile } from '@/api/core/auth.api'
import { setTokens, clearTokens } from '@/lib/token'
import type { LoginRequest } from '@/types/auth.types'

/**
 * Hook pro přihlášení uživatele
 */
export const useLogin = () => {
  const navigate = useNavigate()
  const loginStore = useAuthStore((state) => state.login)

  return useMutation({
    mutationFn: (credentials: LoginRequest) => loginApi(credentials),
    onSuccess: (response) => {
      // Uložit tokeny
      setTokens(response.accessToken, response.refreshToken)
      
      // Uložit user a modules do store
      loginStore(response.user, response.modules)
      
      toast.success('Přihlášení proběhlo úspěšně')
      navigate('/dashboard')
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Přihlášení se nezdařilo'
      toast.error(message)
    },
  })
}

/**
 * Hook pro odhlášení uživatele
 */
export const useLogout = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const logoutStore = useAuthStore((state) => state.logout)

  return useMutation({
    mutationFn: logoutApi,
    onSuccess: () => {
      clearTokens()
      logoutStore()
      queryClient.clear()
      toast.success('Odhlášení proběhlo úspěšně')
      navigate('/login')
    },
    onError: () => {
      // I při chybě odhlásíme uživatele lokálně
      clearTokens()
      logoutStore()
      queryClient.clear()
      navigate('/login')
    },
  })
}

/**
 * Hook pro získání aktuálního uživatele
 */
export const useAuth = () => {
  const user = useAuthStore((state) => state.user)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  return {
    user,
    isAuthenticated,
  }
}

/**
 * Hook pro kontrolu oprávnění
 */
export const usePermissions = () => {
  const canAccess = useAuthStore((state) => state.canAccess)
  const modules = useAuthStore((state) => state.modules)

  return {
    canAccess,
    modules,
  }
}

/**
 * Hook pro načtení profilu uživatele
 */
export const useUserProfile = () => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const setUser = useAuthStore((state) => state.setUser)

    const query = useQuery({
        queryKey: ['userProfile'],
        queryFn: getUserProfile,
        enabled: isAuthenticated,
        staleTime: 5 * 60 * 1000, // 5 minut
    })

    useEffect(() => {
        if (query.data) {
            setUser(query.data)
        }
    }, [query.data, setUser])

    return query
}
